import React, { useState, useRef, useEffect } from "react";
import { LogOut, Settings, MapPin } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import LoginMenu from "./LoginMenu";

interface UserMenuProps {
  isDarkMode: boolean;
  onOpenMyShops: () => void;
  onOpenSettings: () => void;
  onMenuStateChange?: (isOpen: boolean, closeFn?: () => void) => void;
}

const UserMenu: React.FC<UserMenuProps> = ({ isDarkMode, onOpenMyShops, onOpenSettings, onMenuStateChange }) => {
  const { user, logout, loading } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && buttonRef.current && !menuRef.current.contains(event.target as Node) && !buttonRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Notify parent when menu state changes and provide close function
  useEffect(() => {
    onMenuStateChange?.(isMenuOpen, () => setIsMenuOpen(false));
  }, [isMenuOpen, onMenuStateChange]);

  // Don't render anything while auth is still initializing
  if (loading) {
    return null;
  }

  const handleLogout = async () => {
    setIsLoading(true);
    setIsMenuOpen(false);
    try {
      await logout();
    } catch (error) {
      console.error("Failed to log out:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getUserInitial = () => {
    if (!user?.displayName) return "U";
    return user.displayName.charAt(0).toUpperCase();
  };

  const handleMenuItemClick = (action: () => void) => {
    action();
    setIsMenuOpen(false);
  };

  // Sign in menu for non-authenticated users
  if (!user) {
    return <LoginMenu isDarkMode={isDarkMode} onMenuStateChange={onMenuStateChange} onOpenSettings={onOpenSettings} />;
  }

  // User menu for authenticated users
  return (
    <div className="relative">
      {/* User Avatar Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        className="min-w-[44px] min-h-[44px] bg-blue-600 hover:bg-blue-700 text-white rounded-full flex items-center justify-center font-medium text-sm transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
        title={user.displayName || "User menu"}
        aria-label={`Open user menu${user.displayName ? ` for ${user.displayName}` : ''}`}
      >
        {getUserInitial()}
      </button>

      {/* Dropdown Menu */}
      {isMenuOpen && (
        <div
          ref={menuRef}
          className={`absolute w-64 py-2 overflow-hidden
            ${/* Align with navbar edge - right edge with padding */ ""}
            -right-4 md:-right-4
            ${/* Add gap between navbar and dropdown */ ""}
            md:mt-6 mb-6 md:mb-0
            ${/* Match navbar styling exactly - semi-transparent borders */ ""}
            ${isDarkMode ? "bg-gray-800 border-gray-700/20" : "bg-white border-white/20"} 
            shadow-lg rounded-2xl border
            ${/* Mobile positioning - above navbar with gap */ ""}
            bottom-full md:bottom-auto md:top-full
            ${/* Higher z-index to appear above all modals including list view */ ""}
            z-[300]
          `}
        >
          {/* User Info Header */}
          <div className={`px-4 py-3 border-b ${isDarkMode ? "border-gray-700/20" : "border-gray-200"}`}>
            <p className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-black"}`}>{user.displayName || "User"}</p>
            <p className={`text-xs truncate ${isDarkMode ? "text-gray-400" : "text-black"}`}>{user.email}</p>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            {/* My Shops */}
            <button
              onClick={() => handleMenuItemClick(onOpenMyShops)}
              className={`w-full px-4 py-3 text-left text-sm flex items-center gap-3 transition-colors ${isDarkMode
                ? "text-gray-300 hover:bg-gray-700"
                : "text-black hover:bg-gray-50"
                }`}
            >
              <MapPin size={16} />
              My Shops
            </button>

            {/* Settings */}
            <button
              onClick={() => handleMenuItemClick(onOpenSettings)}
              className={`w-full px-4 py-3 text-left text-sm flex items-center gap-3 transition-colors ${isDarkMode
                ? "text-gray-300 hover:bg-gray-700"
                : "text-black hover:bg-gray-50"
                }`}
            >
              <Settings size={16} />
              Settings
            </button>

            {/* Divider */}
            <div className={`border-t ${isDarkMode ? "border-gray-700/20" : "border-gray-200"} my-2 mx-4`}></div>

            {/* Sign Out */}
            <button
              onClick={handleLogout}
              disabled={isLoading}
              className="w-full px-4 py-3 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <LogOut size={16} />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
